// src/pages/.../CraftsmenList.js
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/axiosClient';
import { useAuth } from '../../context/AuthContext'; // adjust path if your context lives elsewhere
import pipersImage from '../../assets/pipers.jpg';

/* ═══════════════ config & helpers ═══════════════ */
const isDev = process.env.NODE_ENV === 'development';
const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || (isDev ? 'https://staging.kaakazini.com' : '');

// Route of the public profile page. Landing + Services use "/craftsmen/:id".
// If your router actually uses "/craftsman/:slug", change this ONE line.
const PROFILE_PATH = '/craftsmen';

// Add real URLs to show the social icons. Empty ones are hidden (no dead "#" links).
const SOCIALS = [
  { icon: 'fab fa-facebook-f',  label: 'Facebook',  url: '' },
  { icon: 'fab fa-x-twitter',   label: 'X',         url: '' },
  { icon: 'fab fa-instagram',   label: 'Instagram', url: '' },
  { icon: 'fab fa-linkedin-in', label: 'LinkedIn',  url: '' },
];

const norm = (s) => (s || '').toLowerCase();
const getSubLabel = (sv) => (typeof sv === 'string' ? sv : sv?.name || sv?.title || '');
const countLabel = (n) => `${n} ${n === 1 ? 'craftsman' : 'craftsmen'}`;

const avatarFallback = (name, size = 400) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Craftsman')}&background=fff000&color=000000&size=${size}&bold=true`;

const mediaUrl = (path) => {
  const p = (path || '').trim();
  if (!p) return null;
  return p.startsWith('http') ? p : `${MEDIA_URL}${p}`;
};

function SafeImg({ src, fallback, alt = '', ...rest }) {
  const [bad, setBad] = useState(false);
  useEffect(() => { setBad(false); }, [src]);
  return <img src={bad || !src ? fallback : src} alt={alt} onError={() => setBad(true)} {...rest} />;
}

/* ═══════════════ page ═══════════════ */
function CraftsmenList() {
  const { user } = useAuth();
  const [craftsmen, setCraftsmen] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    api
      .get('/public-craftsman/', { params: { is_approved: true }, signal: ctrl.signal })
      .then((response) => {
        const data = response.data;
        setCraftsmen(Array.isArray(data) ? data : data?.results || []);
      })
      .catch((err) => {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        console.error('Fetch Error:', err);
        setError('Failed to load craftsmen. Please try again later.');
      })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  }, [reloadKey]);

  // Search matches what the placeholder promises: name, trade, location (and sub-services)
  const filteredCraftsmen = useMemo(() => {
    const q = norm(searchTerm).trim();
    if (!q) return craftsmen;
    return craftsmen.filter((c) =>
      [c.full_name, c.name, c.primary_service, c.profession, c.location, (c.services || []).map(getSubLabel).join(' ')]
        .some((v) => norm(v).includes(q))
    );
  }, [craftsmen, searchTerm]);

  // Hire goes to the client hire page when logged in as a client, otherwise to login.
  // The provider id is passed along so the hire page can pre-select them.
  const hireBase = user?.role === 'client' ? '/hire' : '/HireLogin';
  const visibleSocials = SOCIALS.filter((s) => s.url);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;500;600;700;800;900&display=swap');

        @keyframes shimmer   { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes floatUp   { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
        @keyframes heroSlide { 0%{transform:scale(1)} 100%{transform:scale(1.04)} }
        @keyframes fadeUp    { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }

        :root {
          --yellow:      #fff000;
          --yellow-dark: #e0d800;
          --black:       #000000;
          --white:       #ffffff;
          --bg:          #ffffff;
          --text:        #000000;
          --muted:       #5a5a5a;
          --border:      #e5e5e5;
        }
        body { background:var(--bg); font-family:'Raleway',sans-serif; color:var(--text); overflow-x:hidden; }
        img { max-width:100%; }
        a:focus-visible, button:focus-visible, input:focus-visible { outline:3px solid var(--yellow-dark); outline-offset:2px; }

        /* ── BUTTONS ── */
        .kk-btn-gold { display:inline-flex; align-items:center; gap:7px; background:var(--yellow); color:var(--black); border:none; border-radius:12px; padding:13px 26px; font-weight:700; font-size:.93rem; text-decoration:none; cursor:pointer; transition:all .2s; box-shadow:0 4px 18px rgba(255,240,0,.35); font-family:inherit; }
        .kk-btn-gold:hover { background:var(--yellow-dark); color:var(--black); transform:translateY(-2px); box-shadow:0 8px 28px rgba(255,240,0,.45); }
        .kk-card-actions { display:flex; gap:8px; margin-top:auto; }
        .kk-btn-hire-sm { flex:1.4; display:block; text-align:center; background:var(--yellow); color:var(--black); border:1.5px solid var(--yellow); border-radius:10px; padding:10px 0; font-weight:700; font-size:.84rem; text-decoration:none; cursor:pointer; transition:all .18s; font-family:inherit; box-shadow:0 3px 12px rgba(255,240,0,.28); }
        .kk-btn-hire-sm:hover { background:var(--yellow-dark); border-color:var(--yellow-dark); color:var(--black); transform:translateY(-1px); }
        .kk-btn-outline-sm { flex:1; display:block; text-align:center; background:#fff; color:var(--black); border:1.5px solid var(--black); border-radius:10px; padding:10px 0; font-weight:700; font-size:.84rem; text-decoration:none; cursor:pointer; transition:all .18s; font-family:inherit; }
        .kk-btn-outline-sm:hover { background:var(--black); color:#fff; transform:translateY(-1px); }

        /* ── HERO ── */
        .cl-hero { position:relative; background:var(--black); overflow:hidden; min-height:420px; }
        .cl-hero-bg { position:absolute; inset:0; background-size:cover; background-position:center; opacity:.4; animation:heroSlide 14s ease-in-out infinite alternate; }
        .cl-hero-overlay { position:absolute; inset:0; background:linear-gradient(to bottom,rgba(0,0,0,.45) 0%,rgba(0,0,0,.75) 100%); }
        .cl-hero-inner { position:relative; z-index:2; display:flex; flex-direction:column; align-items:center; text-align:center; gap:20px; padding:72px 20px 56px; animation:floatUp .85s ease both; }
        .cl-hero-title { font-size:clamp(1.9rem,5vw,3.2rem); font-weight:800; color:#fff; line-height:1.15; margin:0; max-width:680px; }
        .cl-hero-title em { font-style:normal; color:var(--yellow); }

        /* search pill — one unified pill at every screen size */
        .cl-search-wrap { width:100%; max-width:620px; position:relative; }
        .cl-search-icon { position:absolute; left:18px; top:50%; transform:translateY(-50%); color:var(--muted); font-size:.88rem; pointer-events:none; z-index:2; }
        .cl-search { width:100%; border:2px solid rgba(255,255,255,.2); border-radius:50px; padding:14px 140px 14px 46px; font-family:inherit; font-size:.93rem; font-weight:500; color:var(--text); background:rgba(255,255,255,.95); outline:none; transition:border-color .2s,box-shadow .2s; box-shadow:0 4px 24px rgba(0,0,0,.25); }
        .cl-search::placeholder { color:#6f6a66; }
        .cl-search:focus { border-color:var(--yellow); background:#fff; box-shadow:0 0 0 4px rgba(255,240,0,.22),0 4px 24px rgba(0,0,0,.15); }
        .cl-search-btn { position:absolute; right:5px; top:50%; transform:translateY(-50%); background:var(--yellow); color:var(--black); border:none; border-radius:50px; padding:10px 22px; font-family:inherit; font-weight:700; font-size:.88rem; cursor:pointer; transition:all .2s; white-space:nowrap; box-shadow:0 4px 14px rgba(255,240,0,.4); display:flex; align-items:center; gap:6px; }
        .cl-search-btn:hover { background:var(--yellow-dark); }

        /* ── CONTENT ── */
        .cl-content { padding:44px 0 72px; background:var(--bg); }
        .cl-count-bar { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; margin-bottom:28px; padding-bottom:18px; border-bottom:1.5px solid var(--border); }
        .cl-count-tag { font-size:.88rem; font-weight:600; color:var(--muted); margin:0; }
        .cl-count-tag strong { color:var(--black); background:#f2f2f2; padding:2px 10px; border-radius:20px; font-size:.82rem; }
        .cl-clear { background:#fff; border:1.5px solid #e0e0e0; color:var(--muted); border-radius:20px; padding:5px 15px; font-size:.81rem; font-weight:600; cursor:pointer; display:inline-flex; align-items:center; gap:6px; transition:all .15s; font-family:inherit; }
        .cl-clear:hover { background:#fee2e2; color:#dc2626; border-color:#fca5a5; }

        /* ── CARDS ── */
        .kk-card { background:#fff; border-radius:14px; overflow:hidden; box-shadow:0 2px 16px rgba(0,0,0,.07); transition:transform .25s,box-shadow .25s,border-color .2s; display:flex; flex-direction:column; height:100%; border:1.5px solid var(--border); position:relative; animation:fadeUp .4s ease both; }
        .kk-card::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background:linear-gradient(to right,var(--black),var(--yellow)); transform:scaleX(0); transform-origin:left; transition:transform .3s; z-index:1; }
        .kk-card:hover { transform:translateY(-5px); box-shadow:0 12px 36px rgba(0,0,0,.12); border-color:var(--yellow); }
        .kk-card:hover::before { transform:scaleX(1); }
        .kk-card-cover { position:relative; height:200px; overflow:hidden; flex-shrink:0; }
        .kk-card-img { width:100%; height:100%; object-fit:cover; transition:transform .4s; }
        .kk-card:hover .kk-card-img { transform:scale(1.06); }
        .kk-card-overlay { position:absolute; inset:0; background:linear-gradient(to top,rgba(0,0,0,.45) 0%,transparent 55%); }
        .kk-badge-verified { position:absolute; top:10px; right:10px; z-index:2; background:var(--yellow); color:var(--black); border-radius:20px; padding:3px 10px; font-size:.67rem; font-weight:800; display:inline-flex; align-items:center; gap:4px; }
        .kk-badge-trade { position:absolute; bottom:10px; left:10px; z-index:2; background:rgba(0,0,0,.82); color:#fff; border-radius:20px; padding:3px 11px; font-size:.7rem; font-weight:700; backdrop-filter:blur(4px); max-width:calc(100% - 20px); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .kk-card-body { padding:16px; display:flex; flex-direction:column; gap:9px; flex:1; }
        .kk-card-name { font-size:1rem; font-weight:700; color:var(--text); margin:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .kk-card-loc { font-size:.74rem; color:var(--muted); display:flex; align-items:center; gap:5px; font-weight:600; }
        .kk-card-loc span { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .kk-svc-tag { background:#f2f2f2; color:var(--black); border-radius:20px; padding:2px 9px; font-size:.69rem; font-weight:600; }
        .kk-skel { background:linear-gradient(90deg,#f0f0f0 25%,#e4e4e4 50%,#f0f0f0 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-radius:8px; }

        /* empty / error */
        .cl-empty { text-align:center; padding:64px 24px; background:var(--white); border-radius:16px; border:1.5px solid var(--border); }
        .cl-empty h5 { font-size:1.2rem; color:var(--text); font-weight:800; margin-bottom:8px; }
        .cl-empty p { color:var(--muted); font-size:.91rem; margin:0 0 16px; }

        /* ── CTA BANNER ── */
        .cl-banner { background:#f2f2f2; padding:52px 0; }
        .cl-banner-inner { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:24px; }
        .cl-banner h2 { font-size:clamp(1.5rem,3vw,2.1rem); font-weight:800; color:var(--black); margin:0; line-height:1.2; }
        .cl-banner p { color:var(--muted); font-size:.93rem; margin:6px 0 0; }

        /* ── FOOTER ── */
        .cl-footer { background:var(--black); color:#bbb; padding:52px 0 26px; }
        .cl-footer h5 { color:#fff; font-weight:700; font-size:.8rem; letter-spacing:.08em; text-transform:uppercase; margin-bottom:14px; }
        .cl-footer a { color:#bbb; text-decoration:none; font-size:.85rem; transition:color .15s; }
        .cl-footer a:hover { color:var(--yellow); }
        .cl-footer p { font-size:.85rem; margin-bottom:6px; }
        .cl-footer-hr { border-color:rgba(255,255,255,.08); margin:26px 0 18px; }
        .cl-footer-social a { font-size:1rem; color:#bbb; margin-right:14px; display:inline-block; transition:color .15s,transform .15s; }
        .cl-footer-social a:hover { color:var(--yellow); transform:translateY(-2px); }
        .cl-footer-map-wrap { border-radius:12px; overflow:hidden; box-shadow:0 4px 20px rgba(0,0,0,.3); }

        /* ── RESPONSIVE ── */
        @media(max-width:992px){
          .cl-hero-inner { padding:64px 18px 48px; gap:18px; }
          .kk-card-cover { height:180px; }
        }
        @media(max-width:768px){
          .cl-hero { min-height:0; }
          .cl-hero-inner { padding:52px 16px 40px; gap:16px; }
          .cl-hero-title { max-width:100%; }
          .cl-search { padding:13px 110px 13px 44px; font-size:.9rem; }
          .cl-search-icon { left:16px; }
          .cl-search-btn { padding:9px 16px; font-size:.82rem; gap:5px; }
          .cl-content { padding:36px 0 56px; }
          .cl-count-bar { margin-bottom:22px; }
          .cl-banner { padding:40px 0; }
          .cl-banner-inner { flex-direction:column; text-align:center; align-items:center; }
          .cl-banner-inner .kk-btn-gold { width:100%; max-width:320px; justify-content:center; }
          .cl-footer { padding:40px 0 22px; }
          .cl-footer .row { gap:0; }
          .cl-footer .row > [class^="col-"] { margin-bottom:28px; }
          .cl-footer-map-wrap iframe { height:180px; }
        }
        @media(max-width:576px){
          .cl-count-bar { flex-direction:column; align-items:flex-start; }
          .cl-hero-title { font-size:1.7rem; }
          .kk-card-body { padding:14px; gap:7px; }
          .kk-card-cover { height:170px; }
          .cl-empty { padding:48px 18px; }
          .cl-banner h2 { font-size:1.4rem; }
          .cl-search { padding:12px 92px 12px 42px; font-size:.86rem; }
          .cl-search-btn { padding:8px 13px; font-size:.78rem; }
        }
        @media(max-width:480px){
          .cl-search { padding:12px 56px 12px 40px; }
          .cl-search-btn { padding:9px 12px; gap:0; }
          .cl-search-btn-label { display:none; }
        }
        @media(max-width:420px){
          .cl-hero-title { font-size:1.5rem; }
          .cl-hero-inner { padding:44px 14px 34px; }
          .cl-search-icon { left:14px; font-size:.8rem; }
          .kk-badge-verified, .kk-badge-trade { font-size:.63rem; padding:2px 8px; }
          .kk-card-name { font-size:.93rem; }
        }
        @media (prefers-reduced-motion: reduce){
          *, *::before, *::after { animation-duration:.01ms!important; animation-iteration-count:1!important; transition-duration:.01ms!important; }
        }
      `}</style>

      <div style={{ background: 'var(--bg)', minHeight: '100vh', fontFamily: "'Raleway', sans-serif" }}>

        {/* ━━━━━━━━━━ HERO ━━━━━━━━━━ */}
        <header className="cl-hero" id="top">
          <div className="cl-hero-bg" style={{ backgroundImage: `url(${pipersImage})` }} aria-hidden="true" />
          <div className="cl-hero-overlay" aria-hidden="true" />
          <div className="container">
            <div className="cl-hero-inner">
              <h1 className="cl-hero-title">
                Find &amp; Hire <em>Skilled</em><br />Craftsmen Near You
              </h1>

              {/* Results update as you type; pressing Enter / the button just closes the keyboard on mobile */}
              <form
                className="cl-search-wrap"
                role="search"
                onSubmit={(e) => { e.preventDefault(); e.currentTarget.querySelector('input')?.blur(); }}
              >
                <i className="fas fa-search cl-search-icon" aria-hidden="true" />
                <input
                  type="search"
                  className="cl-search"
                  placeholder="Search craftsmen by name, trade or location…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  aria-label="Search craftsmen"
                />
                <button type="submit" className="cl-search-btn" aria-label="Search">
                  <i className="fas fa-search" aria-hidden="true" />
                  <span className="cl-search-btn-label">Search</span>
                </button>
              </form>
            </div>
          </div>
        </header>

        {/* ━━━━━━━━━━ GRID ━━━━━━━━━━ */}
        <main className="cl-content">
          <div className="container">
            {!loading && !error && (
              <div className="cl-count-bar" aria-live="polite">
                <p className="cl-count-tag">
                  Showing&nbsp;<strong>{countLabel(filteredCraftsmen.length)}</strong>
                  {searchTerm.trim() && <>&nbsp;matching &ldquo;<strong>{searchTerm.trim()}</strong>&rdquo;</>}
                </p>
                {searchTerm && (
                  <button className="cl-clear" onClick={() => setSearchTerm('')}>
                    <i className="fas fa-times" aria-hidden="true" /> Clear search
                  </button>
                )}
              </div>
            )}

            {loading ? (
              <div className="row g-4">
                {[...Array(8)].map((_, i) => (
                  <div className="col-sm-6 col-md-4 col-lg-3" key={i}>
                    <div style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', border: '1.5px solid var(--border)' }}>
                      <div className="kk-skel" style={{ height: 200 }} />
                      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div className="kk-skel" style={{ height: 13, width: '65%' }} />
                        <div className="kk-skel" style={{ height: 10, width: '42%' }} />
                        <div className="kk-skel" style={{ height: 10, width: '78%' }} />
                        <div className="kk-skel" style={{ height: 40, borderRadius: 10, marginTop: 6 }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="cl-empty" role="alert">
                <h5>Something went wrong</h5>
                <p>{error}</p>
                <button className="kk-btn-gold" onClick={() => setReloadKey((k) => k + 1)}>Try Again</button>
              </div>
            ) : filteredCraftsmen.length === 0 ? (
              <div className="cl-empty">
                <h5>No craftsmen found</h5>
                <p>Try a different name, trade or location, or clear your search to browse everyone.</p>
                <button className="kk-btn-gold" onClick={() => setSearchTerm('')}>Clear Search</button>
              </div>
            ) : (
              <div className="row g-4">
                {filteredCraftsmen.map((craftsman, idx) => {
                  const id = craftsman.slug || craftsman.id;
                  const name = craftsman.full_name || craftsman.name || 'Craftsman';
                  const trade = craftsman.primary_service || craftsman.profession;
                  const subs = (craftsman.services || []).map(getSubLabel).filter(Boolean);
                  return (
                    <div className="col-sm-6 col-md-4 col-lg-3" key={craftsman.id ?? idx} style={{ animationDelay: `${(idx % 8) * 60}ms` }}>
                      <div className="kk-card">
                        <div className="kk-card-cover">
                          {/* Real photo if there is one; otherwise the craftsman's initials — never a random stock face */}
                          <SafeImg
                            src={mediaUrl(craftsman.profile)}
                            fallback={avatarFallback(name)}
                            alt={name}
                            className="kk-card-img"
                            loading="lazy"
                          />
                          <div className="kk-card-overlay" aria-hidden="true" />
                          <span className="kk-badge-verified">
                            <i className="fas fa-check-circle" aria-hidden="true" /> Verified
                          </span>
                          {trade && <span className="kk-badge-trade">{trade}</span>}
                        </div>
                        <div className="kk-card-body">
                          <h3 className="kk-card-name">{name}</h3>
                          {craftsman.location && (
                            <div className="kk-card-loc">
                              <i className="fas fa-map-marker-alt" aria-hidden="true" />
                              <span>{craftsman.location}</span>
                            </div>
                          )}
                          {subs.length > 0 && (
                            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                              {subs.slice(0, 2).map((s, si) => <span className="kk-svc-tag" key={si}>{s}</span>)}
                              {subs.length > 2 && <span className="kk-svc-tag">+{subs.length - 2} more</span>}
                            </div>
                          )}
                          {id ? (
                            <div className="kk-card-actions">
                              <Link to={`${PROFILE_PATH}/${id}`} className="kk-btn-hire-sm">View Profile</Link>
                              <Link to={`${hireBase}?craftsman=${encodeURIComponent(id)}`} className="kk-btn-outline-sm">Hire</Link>
                            </div>
                          ) : (
                            <span className="kk-btn-outline-sm" style={{ marginTop: 'auto', opacity: .5, cursor: 'default' }}>Unavailable</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>

        {/* ━━━━━━━━━━ CTA BANNER ━━━━━━━━━━ */}
        <section className="cl-banner">
          <div className="container">
            <div className="cl-banner-inner">
              <div>
                <h2>Are you a skilled craftsman?</h2>
                <p>Join KaaKazini free — get approved and start receiving job requests.</p>
              </div>
              <Link to="/signup" className="kk-btn-gold">
                <i className="fas fa-user-plus" aria-hidden="true" /> Join as Craftsman — Free
              </Link>
            </div>
          </div>
        </section>

        {/* ━━━━━━━━━━ FOOTER ━━━━━━━━━━ */}
        <footer className="cl-footer">
          <div className="container">
            <div className="row g-5">
              <div className="col-lg-3 col-md-6">
                <h5 style={{ fontSize: '1.4rem', fontWeight: 800, textTransform: 'none', letterSpacing: '-.01em', color: '#fff', marginBottom: 10 }}>
                  KaaKazini
                </h5>
                <p style={{ fontSize: '.84rem', color: '#bbb', lineHeight: 1.75, maxWidth: 240 }}>
                  Connecting skilled craftsmen with clients across Kenya. Verified, trusted, and professional.
                </p>
                {visibleSocials.length > 0 && (
                  <div className="cl-footer-social mt-3">
                    {visibleSocials.map(({ icon, label, url }) => (
                      <a key={label} href={url} target="_blank" rel="noopener noreferrer" aria-label={label}><i className={icon} aria-hidden="true" /></a>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-lg-3 col-md-6">
                <h5>Quick Links</h5>
                <ul className="list-unstyled" style={{ lineHeight: '2.2' }}>
                  <li><Link to="/">Home</Link></li>
                  <li><Link to="/services">View All Services</Link></li>
                  <li><Link to="/signup">Become a Craftsman</Link></li>
                  <li><Link to="/HireLogin">Hire a Craftsman</Link></li>
                </ul>
              </div>
              <div className="col-lg-2 col-md-6">
                <h5>Contact Us</h5>
                <p><i className="fas fa-map-marker-alt me-2" style={{ color: 'var(--yellow)' }} aria-hidden="true" />Kisumu, Kenya</p>
                <p><i className="fas fa-envelope me-2" style={{ color: 'var(--yellow)' }} aria-hidden="true" />support@kaakazini.com</p>
              </div>
              <div className="col-lg-4 col-md-6">
                <h5>Find Us</h5>
                <div className="cl-footer-map-wrap">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d63828.69947405925!2d34.7106301!3d-0.1022054!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182aa5b2e0a70b83%3A0x36005f520589fdfc!2sKisumu!5e0!3m2!1sen!2ske!4v1718888888888!5m2!1sen!2ske"
                    width="100%" height="200" style={{ border: 0, display: 'block' }}
                    allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                    title="Kisumu Location Map"
                  />
                </div>
              </div>
            </div>
            <hr className="cl-footer-hr" />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, fontSize: '.8rem' }}>
              <p style={{ margin: 0 }}>© {new Date().getFullYear()} <strong style={{ color: '#fff' }}>KaaKazini</strong>. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export default CraftsmenList;